<script setup lang="ts">
import { computed, ref, type VNodeChild } from 'vue'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, MoreHorizontal } from '@lucide/vue'
import { calculatePageCount, clampPage, generatePageItems, type PageItem } from '../../utils/pagination'
import RenderContent from '../internal/RenderContent'
import { useUiConfig } from '../config-provider/context'

import type { PaginationProps } from './types'

const props = withDefaults(defineProps<PaginationProps>(), { defaultCurrent: 1, defaultPageSize: 10, total: 0, pageSizeOptions: () => ['10', '20', '50', '100'], loading: false, disabled: undefined, showTotal: false, showSizeChanger: undefined, showQuickJumper: false, hideOnSinglePage: false, align: 'between', simple: false })
const emit = defineEmits<{
  'update:current': [current: number]
  'update:pageSize': [size: number]
  change: [current: number, size: number]
  showSizeChange: [current: number, size: number]
}>()
const config = useUiConfig()
const innerCurrent = ref(props.defaultCurrent)
const innerSize = ref(props.defaultPageSize)
const pageSize = computed(() => props.pageSize ?? innerSize.value)
const pageCount = computed(() => calculatePageCount(props.total, pageSize.value))
const activeCurrent = computed(() => clampPage(props.current ?? innerCurrent.value, pageCount.value))
const shouldRender = computed(() => !props.hideOnSinglePage || pageCount.value > 1)
const pageItems = computed<PageItem[]>(() => generatePageItems(activeCurrent.value, pageCount.value))
const isDisabled = computed(() => props.loading || (props.disabled ?? config.value.componentDisabled ?? false))
const size = computed(() => props.size ?? (config.value.componentSize === 'small' ? 'small' : 'default'))
const showSizeChanger = computed(() => props.showSizeChanger ?? props.total > 50)
const jumpValue = ref('')
const hoverPrevFive = ref(false)
const hoverNextFive = ref(false)
const range = computed<[number, number]>(() => props.total ? [(activeCurrent.value - 1) * pageSize.value + 1, Math.min(activeCurrent.value * pageSize.value, props.total)] : [0, 0])
const totalText = computed(() => typeof props.showTotal === 'function' ? props.showTotal(props.total, range.value) : `共 ${props.total} 条`)
function changePage(target: number) {
  const next = clampPage(target, pageCount.value)
  if (isDisabled.value || next === activeCurrent.value) return
  innerCurrent.value = next
  emit('update:current', next)
  emit('change', next, pageSize.value)
}
function changePageSize(event: Event) {
  const nextSize = Number((event.target as HTMLSelectElement).value)
  if (isDisabled.value || nextSize === pageSize.value || !props.pageSizeOptions.some(option => Number(option) === nextSize)) return
  const current = clampPage(activeCurrent.value, calculatePageCount(props.total, nextSize))
  innerSize.value = nextSize
  innerCurrent.value = current
  emit('update:pageSize', nextSize)
  emit('update:current', current)
  emit('showSizeChange', current, nextSize)
  emit('change', current, nextSize)
}
function handleQuickJump() {
  const next = Number(jumpValue.value)
  if (Number.isInteger(next) && next >= 1 && next <= pageCount.value) changePage(next)
  jumpValue.value = ''
}
function jumpPrevFive() { changePage(Math.max(1, activeCurrent.value - 5)) }
function jumpNextFive() { changePage(Math.min(pageCount.value, activeCurrent.value + 5)) }
</script>

<template>
  <nav
    v-if="shouldRender"
    class="rr-pagination"
    :class="[
      `is-align-${align}`,
      `is-size-${size}`,
      { 'is-disabled': isDisabled, 'is-simple': simple }
    ]"
    aria-label="分页导航"
  >
    <!-- Total Info -->
    <div v-if="showTotal && !simple" class="rr-pagination__total">
      <slot name="total" :total="total" :range="range">
        <RenderContent :value="totalText" />
      </slot>
    </div>

    <div class="rr-pagination__controls">
      <!-- PageSize Sizer -->
      <div v-if="showSizeChanger && !simple" class="rr-pagination__sizer">
        <select
          :value="pageSize"
          :disabled="isDisabled"
          aria-label="每页条数"
          @change="changePageSize"
        >
          <option v-for="option in pageSizeOptions" :key="option" :value="option">
            {{ option }} {{ config.locale?.Pagination?.items_per_page ?? '条/页' }}
          </option>
        </select>
      </div>

      <!-- Simple Mode -->
      <div v-if="simple" class="rr-pagination__simple">
        <button
          type="button"
          class="rr-pagination__btn rr-pagination__btn--icon"
          :disabled="isDisabled || activeCurrent <= 1"
          aria-label="上一页"
          @click="changePage(activeCurrent - 1)"
        >
          <ChevronLeft :size="15" aria-hidden="true" />
        </button>
        <span class="rr-pagination__simple-text">
          {{ activeCurrent }} / {{ pageCount }}
        </span>
        <button
          type="button"
          class="rr-pagination__btn rr-pagination__btn--icon"
          :disabled="isDisabled || activeCurrent >= pageCount"
          aria-label="下一页"
          @click="changePage(activeCurrent + 1)"
        >
          <ChevronRight :size="15" aria-hidden="true" />
        </button>
      </div>

      <!-- Standard Page List -->
      <ul v-else class="rr-pagination__list">
        <!-- Prev Button -->
        <li>
          <button
            type="button"
            class="rr-pagination__btn rr-pagination__btn--icon"
            :disabled="isDisabled || activeCurrent <= 1"
            title="上一页"
            aria-label="上一页"
            @click="changePage(activeCurrent - 1)"
          >
            <ChevronLeft :size="15" aria-hidden="true" />
          </button>
        </li>

        <!-- Page Items -->
        <li v-for="(item, idx) in pageItems" :key="idx">
          <!-- Jump Prev/Next 5 Pages Ellipsis -->
          <button
            v-if="item === '...'"
            type="button"
            class="rr-pagination__btn rr-pagination__jump-btn"
            :disabled="isDisabled"
            :title="idx === 1 ? '向前 5 页' : '向后 5 页'"
            @mouseenter="idx === 1 ? (hoverPrevFive = true) : (hoverNextFive = true)"
            @mouseleave="idx === 1 ? (hoverPrevFive = false) : (hoverNextFive = false)"
            @click="idx === 1 ? jumpPrevFive() : jumpNextFive()"
          >
            <template v-if="idx === 1">
              <ChevronsLeft v-if="hoverPrevFive" :size="14" class="rr-pagination__jump-icon" />
              <MoreHorizontal v-else :size="14" class="rr-pagination__ellipsis-icon" />
            </template>
            <template v-else>
              <ChevronsRight v-if="hoverNextFive" :size="14" class="rr-pagination__jump-icon" />
              <MoreHorizontal v-else :size="14" class="rr-pagination__ellipsis-icon" />
            </template>
          </button>

          <!-- Normal Page Button -->
          <button
            v-else
            type="button"
            class="rr-pagination__btn rr-pagination__btn--page"
            :class="{ 'is-active': item === activeCurrent }"
            :disabled="isDisabled"
            :aria-label="`第 ${item} 页`"
            :aria-current="item === activeCurrent ? 'page' : undefined"
            @click="changePage(item)"
          >
            {{ item }}
          </button>
        </li>

        <!-- Next Button -->
        <li>
          <button
            type="button"
            class="rr-pagination__btn rr-pagination__btn--icon"
            :disabled="isDisabled || activeCurrent >= pageCount"
            title="下一页"
            aria-label="下一页"
            @click="changePage(activeCurrent + 1)"
          >
            <ChevronRight :size="15" aria-hidden="true" />
          </button>
        </li>
      </ul>

      <!-- Quick Jumper -->
      <div
        v-if="showQuickJumper && pageCount > 1 && !simple"
        class="rr-pagination__jumper"
        @keydown.enter.stop.prevent="handleQuickJump"
      >
        <span>{{ config.locale?.Pagination?.jump_to ?? '跳至' }}</span>
        <input
          v-model="jumpValue"
          type="number"
          min="1"
          :max="pageCount"
          :disabled="isDisabled"
          aria-label="跳转目标页码"
          @blur="handleQuickJump"
        />
        <span>{{ config.locale?.Pagination?.page ?? '页' }}</span>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.rr-pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-sizing: border-box;
  width: 100%;
  color: var(--rr-color-text-muted);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  user-select: none;
}

.rr-pagination.is-align-right { justify-content: flex-end; }
.rr-pagination.is-align-center { justify-content: center; }
.rr-pagination.is-align-left { justify-content: flex-start; }
.rr-pagination.is-align-between { justify-content: space-between; }

.rr-pagination__total {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.rr-pagination__controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.rr-pagination__sizer select {
  width: auto !important;
  height: 30px !important;
  min-height: unset !important;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: var(--rr-radius-control);
  background: var(--rr-color-surface);
  padding: 0 8px;
  color: var(--rr-color-text);
  font-size: 12px;
  cursor: pointer;
  outline: 0;
  box-sizing: border-box;
  transition: border-color 150ms ease;
}

.rr-pagination__sizer select:hover:not(:disabled) {
  border-color: var(--rr-color-primary-border);
}

.rr-pagination__list {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.rr-pagination__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px !important;
  max-width: 48px;
  height: 30px !important;
  min-height: 30px !important;
  max-height: 30px !important;
  padding: 0 6px !important;
  margin: 0 !important;
  box-sizing: border-box;
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-control);
  background: var(--rr-color-surface);
  color: var(--rr-color-text);
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: border-color var(--rr-motion-duration-fast) var(--rr-motion-ease-out),
    background-color var(--rr-motion-duration-fast) var(--rr-motion-ease-out),
    color var(--rr-motion-duration-fast) var(--rr-motion-ease-out);
}

.rr-pagination__btn:hover:not(:disabled) {
  border-color: var(--rr-color-primary-border);
  color: var(--rr-color-primary);
  background: var(--rr-color-surface-subtle);
}

.rr-pagination__btn.is-active {
  border-color: var(--rr-color-primary);
  background: var(--rr-color-primary);
  color: var(--rr-color-surface) !important;
  font-weight: 600;
}

.rr-pagination__btn:disabled {
  border-color: var(--rr-color-border);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-subtle);
  cursor: not-allowed;
  opacity: 0.6;
}

.rr-pagination__jump-btn {
  border-color: transparent;
  background: transparent;
  color: var(--rr-color-text-subtle);
}

.rr-pagination__jump-btn:hover:not(:disabled) {
  border-color: transparent;
  color: var(--rr-color-primary);
  background: var(--rr-color-primary-soft);
}

.rr-pagination__jumper {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.rr-pagination__jumper input {
  width: 46px !important;
  height: 30px !important;
  min-height: unset !important;
  padding: 0 4px !important;
  box-sizing: border-box;
  text-align: center;
  font-size: 12px;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: var(--rr-radius-control);
  background: var(--rr-color-surface);
  color: var(--rr-color-text);
  outline: 0;
  transition: border-color 150ms ease;
}

.rr-pagination__jumper input:focus {
  border-color: var(--rr-color-primary);
  box-shadow: 0 0 0 2px var(--rr-color-primary-soft);
}

/* Small Size */
.rr-pagination.is-size-small .rr-pagination__btn {
  min-width: 24px !important;
  height: 24px !important;
  min-height: 24px !important;
  max-height: 24px !important;
  font-size: 11px;
}

.rr-pagination.is-size-small .rr-pagination__sizer select,
.rr-pagination.is-size-small .rr-pagination__jumper input {
  height: 24px !important;
  min-height: 24px !important;
  font-size: 11px;
}
</style>
